import React, { useEffect, useMemo, useState } from 'react';
import { ReportTable } from '../Layouts/ReportTable';
import { authorizeClient } from '../../../networking/client';

import { getOrderIds, SalesOrderInfo, selectSalesOrders } from '../../../store/slices/salesOrders';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { selectDirtyState, setDirtyState } from '../../../store/slices/auth';
import { BioMarkerDetails } from '../../BiomarkerDetails/BioMarkerDetails';
import { ConsultationDetails } from '../../DHPConsultationDetails/DHPConsultationDetails';
import { Documents } from '../../../screens/VideoConsultingRoom/VideoMenu/Documents';
import AnamnesisResultViewer from '../../../screens/Anamnesis/AnamnesisResultViewer';
import CustomerExaminations from '../../../screens/CustomerExaminations/CustomerExaminations';
import VitalSignResultsV2 from '../../../screens/VitalSignResultsV2/VitalSignResultsV2';
import CustomPagination from '../CustomPagination/CustomPagination';
import TabButton from '../Buttons/TabButton';
import BiomarkerCard from '../DHP/BiomarkerCard';
import Spinner from '../Spinner/Spinner';
import chevronRight from '../../../assets/icons/chevron-right-2.svg';
import classes from './DigitalHealthProfileTab.module.css';
import { useTranslation } from '../../../hooks/useTranslations';
import { ICustomer } from '../../../store/slices/customers';

type Props = {
  selectedSalesOrder?: SalesOrderInfo;
  person_account_number?: string;
  get_dhp_from_merchant?: boolean;
  isVideoCall?: boolean;
  hasDocuments?: boolean;
  hasDocumentsPerOrder?: boolean;
  person_id?: number;
  person_external_id?: number;
  anamnesisResultViewer?: boolean;
  customer?: ICustomer;
};

export interface IOnlineConsultation {
  doctor_name: string;
  findings: string;
  investigation: string;
  recommandation: string;
}

export interface IDigitalHealthProfile {
  product_name: string;
  product_group: string;
  diagnose_reference_ranges?: IDiagnoseReferenceRanges[];
  diagnose_result?: IDiagnoseResult[];
  online_consultation: IOnlineConsultation[];
  booking_date: Date;
  id?: string;
  ta_id: number;
  transaction_id: number;
  product_id: number;
  attachments?: IAttachment[];
  start?: Date;
  sample_date?: Date;
  amount_paid?: string;
  vat?: string;
  conversation_id?: string;
  conversation_status?: string;
  conversation_title?: string;
  participants?: Array<any>;
  product_order_item_id_out: number;
  vital_sign_result?: any;
  result_date?: Date;
  video_settings?: Array<string>;
}

export interface IDiagnoseReferenceRanges {
  property_id: number;
  property_range: string;
  range_color_order: number;
  reference_color_code: string;
  reference_range_name: string;
}

export interface IDiagnoseResult {
  color_code: string;
  property_how: string;
  property_id: number;
  property_name: string;
  property_short_abbreviation?: string;
  property_value: string;
  property_what: string;
  property_why: string;
  reference_introductory_statement: string;
  reference_summary?: string;
  reference_range_name: string;
  uom: string;
  property_range: string;
}

export interface IAttachment {
  attachment_type_description: string;
  attachment_type_id: number;
  attachment_id?: number;
  datetime_created?: string;
  description?: string;
  transcription_text?: string;
  link?: string;
  name?: string;
  title?: string;
  file_extension?: string;
  memo?: string;
  fileSize?: number;
  is_uploaded_by_doctor?: boolean;
  person_firstname?: string;
  person_lastname?: string;
  person_designation?: string;
  comment: string;
}

const DigitalHealthProfileTab: React.FC<Props> = ({
  selectedSalesOrder,
  person_account_number,
  // get_dhp_from_merchant,
  isVideoCall,
  hasDocuments,
  hasDocumentsPerOrder,
  person_id,
  person_external_id,
  anamnesisResultViewer,
  customer
}) => {
  const { translations } = useTranslation();
  const dispatch = useAppDispatch();
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const allSalesOrders = useAppSelector<SalesOrderInfo[]>(selectSalesOrders);
  const [dhps, setDhps] = useState<IDigitalHealthProfile[]>([]);
  const [dhpsToDisplay, setDhpsToDisplay] = useState<IDigitalHealthProfile[]>([]);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [selectedDhp, setSelectedDhp] = useState<IDigitalHealthProfile>();
  const [selectedDiagnoseResult, setSelectedDiagnoseResult] = React.useState<IDiagnoseResult>();
  const [selectedOnlineResult, setSelectedOnlineResult] = React.useState<any>();
  const accountNumber = React.useMemo(() => {
    return selectedSalesOrder?.customer_person?.person_account_number ?? person_account_number;
  }, [selectedSalesOrder, person_account_number]);
  const isDirtyState = useAppSelector(selectDirtyState);

  useEffect(() => {
    const fetchDHP = async () => {
      try {
        setIsFetching(true);
        setDhps([]);
        const response = await authorizeClient.get(
          `/core/healthpoint/customer/${accountNumber}/product_orders`
        );
        const _dhps: IDigitalHealthProfile[] =
          response?.data.map((dhp: IDigitalHealthProfile) => {
            return {
              ...dhp,
              id: `${dhp.ta_id}/${dhp.transaction_id}`,
            };
          }) ?? [];
        setDhps(_dhps);

        const _selectedDhp = _dhps.find((dhp) => dhp.id === selectedDhp?.id);

        if (_selectedDhp) {
          setSelectedDhp(_selectedDhp);
          if (selectedDiagnoseResult) {
            setSelectedDiagnoseResult(
              _selectedDhp.diagnose_result?.find(
                (dr) => dr.property_id === selectedDiagnoseResult.property_id
              )
            );
          }
        }
      } catch (err) {
        console.log(err);
      } finally {
        setIsFetching(false);
      }
    };

    if (accountNumber) fetchDHP();
  }, [accountNumber]);

  useEffect(() => {
    if (dhps) {
      setDhpsToDisplay(dhps.filter((dhp) => dhp.diagnose_result));
    }
  }, [dhps]);

  const customerDiagnosticSalesOrdersClosed = useMemo(() => {
    if (dhps) {
      return dhps
        .filter((dhp) => dhp.diagnose_result)
        .map((dhp) => {
          return dhp;
        });
    }
  }, [dhps]);

  const customerConsultationSalesOrdersClosed = useMemo(() => {
    if (dhps && allSalesOrders) {
      return dhps
        .filter(
          (dhp) =>
            (dhp.product_group === 'C' || dhp.product_group === 'F' || dhp.product_group === 'R') &&
            dhp.online_consultation
        )
        .filter((el) => {
          const salesOrderInfo = allSalesOrders.find(
            (s) =>
              s.sales_order?.ta_id == el?.ta_id &&
              s.sales_order?.transaction_id == el?.transaction_id
          );
          return salesOrderInfo?.sales_order_appointment?.sales_order_appointment_status !== 'M';
        })
        .map((dhp) => {
          return dhp;
        });
    }
  }, [dhps, allSalesOrders]);

  if (isFetching) {
    return <Spinner />;
  }

  if (selectedDiagnoseResult && selectedDhp?.diagnose_reference_ranges) {
    return (
      <BioMarkerDetails
        diagnoseResult={selectedDiagnoseResult}
        onSelectResult={(product) => setSelectedDiagnoseResult(product)}
        diagnose_reference_ranges={selectedDhp?.diagnose_reference_ranges}
        product={selectedDhp}
      />
    );
  }

  const handleNavigate = (tab: number) => {
    if (!isDirtyState.isDirty || ['saveTextArea', 'anamnesis'].includes(isDirtyState.component)) {
      setSelectedTab(tab);
      setSelectedDhp(undefined);
      setSelectedOnlineResult(undefined);
    } else {
      dispatch(
        setDirtyState({ isDirty: true, isModalOpen: true, component: isDirtyState.component })
      );
      return;
    }
  };

  const renderAnamnesis = () => {
    if (selectedSalesOrder) {
      return (
        <AnamnesisResultViewer
          personId={selectedSalesOrder?.customer_person?.person_id}
          personExternalId={selectedSalesOrder?.customer_person?.person_external_id}
          disableEdit={Boolean(
            selectedSalesOrder?.work_order?.work_order_status_id == 2 ||
            (anamnesisResultViewer && selectedSalesOrder)
          )}
        />
      );
    }
  };

  return (
    <div className={classes.container}>
      {/* <div className={classes.dhpTitleContainer}>
        <div className={classes.dhpTitleAndDescription}>
          <div className={classes.dhpTitle}>{translations('digital.health.profile')}</div>
          <div className={classes.dhpDescription}>{translations('patient.latest')}</div>
        </div>
      </div> */}
      <div className={classes.dhpTabsContainer}>
        <div className={classes.dhpTabs}>
          <TabButton
            label={translations('biomarkers')}
            onClick={() => handleNavigate(0)}
            isSelected={selectedTab === 0}
            tabStyle={{
              padding: '20px 35px',
              borderBottom: '3px solid #0b1b5c',
              color: '#0b1b5c',
              fontSize: '17px',
            }}
          />
          <TabButton
            label={translations('sidebar.diagnostics')}
            onClick={() => handleNavigate(1)}
            isSelected={selectedTab === 1}
            tabStyle={{
              padding: '20px 35px',
              borderBottom: '3px solid #0b1b5c',
              color: '#0b1b5c',
              fontSize: '17px',
            }}
          />
          <TabButton
            label={translations('consultations')}
            onClick={() => handleNavigate(2)}
            isSelected={selectedTab === 2}
            tabStyle={{
              padding: '20px 35px',
              borderBottom: '3px solid #0b1b5c',
              color: '#0b1b5c',
              fontSize: '17px',
            }}
          />
          <TabButton
            label={translations('anamnesis')}
            onClick={() => handleNavigate(4)}
            isSelected={selectedTab === 4}
            tabStyle={{
              padding: '20px 35px',
              borderBottom: '3px solid #0b1b5c',
              color: '#0b1b5c',
              fontSize: '17px',
            }}
          />
          {(hasDocuments || hasDocumentsPerOrder) && (
            <TabButton
              label={translations('documents')}
              onClick={() => handleNavigate(3)}
              isSelected={selectedTab === 3}
              tabStyle={{
                padding: '20px 35px',
                borderBottom: '3px solid #0b1b5c',
                color: '#0b1b5c',
                fontSize: '17px',
              }}
            />
          )}
          <TabButton
            label={translations('vital.signs')}
            onClick={() => handleNavigate(5)}
            isSelected={selectedTab === 5}
            tabStyle={{
              padding: '20px 35px',
              borderBottom: '3px solid #0b1b5c',
              color: '#0b1b5c',
              fontSize: '17px',
            }}
          />
          <TabButton
            label={translations('examinations')}
            onClick={() => handleNavigate(6)}
            isSelected={selectedTab === 6}
            tabStyle={{
              padding: '20px 35px',
              borderBottom: '3px solid #0b1b5c',
              color: '#0b1b5c',
              fontSize: '17px',
            }}
          />
        </div>
        {selectedTab === 0 && selectedDhp && (
          <div className={classes.biomarkersContainer}>
            <BiomarkerCard
              key={`${selectedDhp.transaction_id}_${selectedDhp.ta_id}`}
              isVideoCall={isVideoCall}
              dhp={selectedDhp}
              onClick={() => setSelectedDhp(undefined)}
              expanded={true}
              onSelectDiagnoseResult={(diagnoseResult, product) => {
                setSelectedDhp(product);
                setSelectedDiagnoseResult(diagnoseResult);
              }}
            />
          </div>
        )}
        {selectedTab === 0 && !selectedDhp && (
          <div className={classes.biomarkersContainer}>
            {dhpsToDisplay.map((dhp, index) => {
              return (
                <BiomarkerCard
                  key={index}
                  dhp={dhp}
                  onClick={() => {
                    setSelectedDhp(dhp)
                  }}
                  expanded={false}
                  onSelectDiagnoseResult={(diagnoseResult, product) => {
                    setSelectedDhp(product);
                    setSelectedDiagnoseResult(diagnoseResult);
                  }}
                  isVideoCall={isVideoCall}
                />
              );
            })}
          </div>
        )}

        {selectedTab === 0 && !selectedDhp && dhpsToDisplay.length === 0 && (
          <div className={classes.emptyStateContainer}>
            <div className="emptyState">{translations('label.empty_state')}</div>
          </div>
        )}

        {selectedTab === 1 && customerDiagnosticSalesOrdersClosed && (
          <>
            {selectedDhp && (
              <div className={classes.biomarkersContainer}>
                <BiomarkerCard
                  dhp={selectedDhp}
                  onClick={() => setSelectedDhp(undefined)}
                  expanded={true}
                  onSelectDiagnoseResult={(diagnoseResult) =>
                    setSelectedDiagnoseResult(diagnoseResult)
                  }
                  isVideoCall={isVideoCall}
                />
              </div>
            )}

            {!selectedDhp && (
              <>
                {customerDiagnosticSalesOrdersClosed.length > 0 ? (
                  <CustomPagination itemsPerPage={10} items={customerDiagnosticSalesOrdersClosed}>
                    {(paginatedItems: any[]) => (
                      <ReportTable
                        columns={[
                          {
                            name: 'ID',
                            path: 'id',
                            type: 'ID',
                          },
                          {
                            name: translations('date'),
                            path: 'sample_date',
                            type: 'DATENUMBERED',
                          },
                          {
                            name: translations('time'),
                            path: 'sample_date',
                            type: 'TIME',
                          },
                          {
                            name: translations('service'),
                            path: 'product_name',
                            type: 'TEXT',
                          },
                        ]}
                        rows={paginatedItems}
                        actions={[
                          {
                            renderer: (
                              <img
                                alt={'chevron-right'}
                                src={chevronRight}
                                style={{ cursor: 'pointer' }}
                              />
                            ),
                            onClick: (row: any) => {
                              setSelectedDhp(row);
                            },
                          },
                        ]}
                      />
                    )}
                  </CustomPagination>
                ) : (
                  <div className={classes.emptyStateContainer}>
                    <div className="emptyState">{translations('label.empty_state')}</div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {selectedTab === 2 && customerConsultationSalesOrdersClosed && (
          <div style={{ boxSizing: 'border-box' }}>
            {selectedOnlineResult ? (
              <ConsultationDetails
                onlineResult={selectedOnlineResult}
                handleClose={() => setSelectedOnlineResult(undefined)}
              />
            ) : customerConsultationSalesOrdersClosed.length > 0 ? (
              <CustomPagination itemsPerPage={10} items={customerConsultationSalesOrdersClosed}>
                {(paginatedItems: any[]) => (
                  <ReportTable
                    columns={[
                      {
                        name: 'ID',
                        path: 'id',
                        type: 'ID',
                      },
                      {
                        name: translations('date'),
                        path: 'start',
                        type: 'DATENUMBERED',
                      },
                      {
                        name: translations('time'),
                        path: 'start',
                        type: 'TIME',
                      },
                      {
                        name: translations('service'),
                        path: 'product_name',
                        type: 'TEXT',
                      },
                    ]}
                    rows={paginatedItems}
                    actions={[
                      {
                        renderer: (
                          <img
                            alt={'chevron-right'}
                            src={chevronRight}
                            style={{ cursor: 'pointer' }}
                          />
                        ),
                        onClick: async (row: any) => {
                          const response = await dispatch(
                            getOrderIds({
                              taId: row.ta_id,
                              productOrderId: row.product_order_id_out,
                              productOrderItemId: row.product_order_item_id_out,
                            })
                          );
                          const orderIds = response.payload.data;
                          setSelectedOnlineResult({ ...row, ...orderIds });
                        },
                      },
                    ]}
                  />
                )}
              </CustomPagination>
            ) : (
              <div className={classes.emptyStateContainer}>
                <div className="emptyState">{translations('label.empty_state')}</div>
              </div>
            )}
          </div>
        )}

        {selectedTab === 3 && (
          <div style={{ margin: '10px' }}>
            <Documents
              personAccountNumber={hasDocumentsPerOrder ? undefined : accountNumber}
              selectedSalesOrder={selectedSalesOrder}
            />
          </div>
        )}
        {selectedTab === 4 &&
          // If selectedSalesOrder dont exists then the DigitalHealthProfileTab is being used from Customers screen.
          (selectedSalesOrder ? (
            renderAnamnesis()
          ) : (
            <AnamnesisResultViewer
              personId={person_id || 0}
              personExternalId={person_external_id || 0}
            />
          ))}
        {selectedTab === 5 && (
          <div style={{ margin: '10px' }}>
            <VitalSignResultsV2
              accountNumber={accountNumber}
              personAccountNumber={
                person_external_id || selectedSalesOrder?.customer_person?.person_external_id
              }
            />
          </div>
        )}
        {selectedTab === 6 && (
          <div style={{ margin: '10px' }}>
            <CustomerExaminations accountNumber={accountNumber} />
          </div>
        )}
      </div>
    </div>
  );
};

export default DigitalHealthProfileTab;
